import React from "react";
import {Animated, Easing, ScrollView, useWindowDimensions} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import Btn from "../../components/Btn";
import Div from "../../lib/Div";
import P from "../../lib/P";
import {formatCurrency} from "../../utils";
import {colors, layout, missingApiValue} from "./theme";
import {WithdrawResultState} from "./types";
import {buildPayoutTrackingSteps, WithdrawTrackingStep} from "./payoutTrackingPolicy";
import {
  flowStyles, OutlineButton, PrimaryButton, ResultGlow, ResultIcon, SignaturePatternLayer, SignatureResultSurface
} from "./WithdrawFlowPrimitives";

type WithdrawResultScreenProps = {
  result: WithdrawResultState, amountValue: number, destinationLabel: string, methodLabel: string, feeLabel: string,
  netAmountLabel: string, onDone: () => void, onRetry: () => void, onViewTransaction: () => void
};

type WithdrawSuccessReceiptLine = { label: string; value: string; accent?: boolean };
type WithdrawProcessingSummaryLine = { label: string; value: string };
type ResultActionButtonProps = { label: string; primary?: boolean; disabled?: boolean; onPress?: () => void };
type WithdrawProcessingScreenProps = {
  amountValue: number, destinationLabel: string, methodLabel: string, reference?: string, onDone: () => void,
  onViewTransaction: () => void, trackingSteps: WithdrawTrackingStep[]
};
type WithdrawUncertainScreenProps = {
  amountValue: number, destinationLabel: string, methodLabel: string, message?: string, onDone: () => void,
  onViewTransaction: () => void
};

const resultPopEase = Easing.bezier(0.2, 0.8, 0.2, 1);

const useResultIconPopStyle = () => {
  const iconProgress = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.timing(iconProgress, {toValue: 1, duration: 360, easing: resultPopEase, useNativeDriver: true}).start();

    return () => iconProgress.stopAnimation();
  }, [iconProgress]);

  return {
    opacity: iconProgress.interpolate({inputRange: [0, 0.6, 1], outputRange: [0, 1, 1]}),
    transform: [{scale: iconProgress.interpolate({inputRange: [0, 0.6, 1], outputRange: [0.7, 1.05, 1]})}]
  };
};

const useSuccessHeroAnimations = () => {
  const iconProgress = React.useRef(new Animated.Value(0)).current;
  const burstProgress = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    let mounted = true;
    let burstDelay: ReturnType<typeof setTimeout> | undefined;

    Animated.timing(iconProgress, {toValue: 1, duration: 360, easing: resultPopEase, useNativeDriver: true}).start();

    const runBurst = () => {
      burstProgress.setValue(0);
      Animated.timing(burstProgress, {
        toValue: 1, duration: 2400, easing: Easing.out(Easing.ease), useNativeDriver: true
      }).start(({finished}) => {
        if (finished && mounted) runBurst();
      });
    };

    burstDelay = setTimeout(runBurst, 500);

    return () => {
      mounted = false;
      if (burstDelay) clearTimeout(burstDelay);
      iconProgress.stopAnimation();
      burstProgress.stopAnimation();
    };
  }, [burstProgress, iconProgress]);

  return {
    burstStyle: {
      opacity: burstProgress.interpolate({inputRange: [0, 0.7, 1], outputRange: [0.45, 0, 0]}),
      transform: [{scale: burstProgress.interpolate({inputRange: [0, 0.7, 1], outputRange: [0.64, 1, 1]})}]
    },
    iconStyle: {
      opacity: iconProgress.interpolate({inputRange: [0, 0.6, 1], outputRange: [0, 1, 1]}),
      transform: [{scale: iconProgress.interpolate({inputRange: [0, 0.6, 1], outputRange: [0.7, 1.05, 1]})}]
    }
  };
};

const WithdrawSuccessHeroIcon = () => {
  const {burstStyle, iconStyle} = useSuccessHeroAnimations();

  return (
    <Div alignItems="center" mt={12} position="relative">
      <ResultGlow tone="success" exactStyle={{zIndex: 0}}/>

      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: 'absolute', left: '50%', top: '50%', width: 124, height: 124, marginLeft: -62, marginTop: -62,
            borderRadius: 999, backgroundColor: colors.statusSuccessBurst, zIndex: 0
          },
          burstStyle
        ]}
      />

      <Animated.View style={[{zIndex: 1}, iconStyle]}>
        <ResultIcon
          icon="check-circle" color={colors.statusSuccess} bg={colors.statusSuccessSoft} rounded={999}
          borderColor={colors.statusSuccessBorder}
        />
      </Animated.View>
    </Div>
  );
};

export const WithdrawSuccessAmountHeadline = ({netAmountLabel}: { netAmountLabel: string }) => (
  <Div alignItems="center" mt={18}>
    <P fontFamily="POI Aeronaut" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase"
       style={{fontWeight: '500', letterSpacing: 1.76}}>
      You're getting
    </P>
    <P fontFamily="POI Orbiter" fs={56} lh={58.8} color={colors.accentText} mt={6}
       fontVariant={['tabular-nums', 'lining-nums']} style={{fontWeight: '700', letterSpacing: -1.12}}>
      {netAmountLabel}
    </P>
  </Div>
);

const WithdrawSuccessReceiptSummary = ({lines}: { lines: WithdrawSuccessReceiptLine[] }) => (
  <Div mt={24} bg={colors.signatureSummary} borderWidth={1} borderColor={colors.surfaceBorder} rounded={16} px={18}
       py={4}>
    {lines.map((line, index) => {
      const isLast = index === lines.length - 1;

      return (
        <Div key={line.label} row justifyContent="space-between" alignItems="center" gap={12} py={12}
             borderBottomWidth={isLast ? 0 : 1} borderBottomColor={colors.hairline}>
          <P fontFamily="POI Aeronaut" fs={13} lh={19.5} color={colors.textSecondary}>{line.label}</P>

          <P fontFamily="POI Aeronaut" fs={14} lh={21} color={line.accent ? colors.accentText : colors.textPrimary}
             ta="right" flex={1} fontVariant={['tabular-nums']} style={{fontWeight: '500'}}>
            {line.value}
          </P>
        </Div>
      );
    })}
  </Div>
);

const WithdrawTrackingTimeline = ({steps}: { steps: WithdrawTrackingStep[] }) => {
  const {width} = useWindowDimensions();
  const contentWidth = Math.min(width, layout.mobileMax) - layout.mobileGutter * 2;
  const trackingInset = contentWidth >= 390 ? 80 : Math.max(48, Math.round(contentWidth * (80 / 390)));

  return (
    <Div mt={22} ml={trackingInset}>
      <P fontFamily="POI Aeronaut" color={colors.textSecondary} fs={11} lh={14.3} tt="uppercase" mb={14}
         style={{fontWeight: '500', letterSpacing: 1.76}}>
        Withdrawal status
      </P>
      <Div>
        {steps.map((step, index) => {
          const last = index === steps.length - 1;
          const active = step.state !== 'future';
          const done = step.state === 'done';
          const future = step.state === 'future';

          return (
            <Div key={`${step.state}-${step.title}`} row alignItems="stretch" gap={14}>
              <Div w={16} alignItems="center">
                <Div w={14} h={14} mt={1} position="relative" alignItems="center" justifyContent="center">
                  {active && (
                    <Div position="absolute" left={-4} top={-4} w={22} h={22} rounded={999} bg={colors.accentSoft}/>
                  )}
                  <Div
                    w={14} h={14} rounded={999} bg={active ? colors.accent : colors.hairlineStrongOnSignatureBg}
                    flexShrink={0}
                  />
                </Div>
                {!last && (
                  <Div
                    flex={1} w={0} borderLeftWidth={2} borderStyle="dotted"
                    borderLeftColor={done ? colors.accent : colors.hairlineStrong} mt={3} mb={3}
                  />
                )}
              </Div>
              <Div flex={1} pb={last ? 0 : 22}>
                <P fontFamily="POI Aeronaut" fs={14} lh={21} color={future ? colors.textMuted : colors.textPrimary}
                   style={{fontWeight: future ? '400' : '500'}}>
                  {step.title}
                </P>
                {step.subtitle && (
                  <P fontFamily="POI Aeronaut" fs={12} lh={18} color={colors.textSecondary} mt={2}>
                    {step.subtitle}
                  </P>
                )}
              </Div>
            </Div>
          );
        })}
      </Div>
    </Div>
  );
};

const ResultActionButton = ({label, primary, disabled, onPress}: ResultActionButtonProps) => (
  <Btn onPress={disabled ? undefined : onPress} disabled={disabled} w="100%" h={52} rounded={layout.buttonRadius}
       bg={primary ? colors.accent : colors.bgInset} alignItems="center" justifyContent="center" px={28}
       opacity={disabled ? 0.45 : undefined} accessibilityRole="button" accessibilityState={{disabled}}>
    <P fontFamily="POI Aeronaut" fs={14} lh={21} tt="uppercase" ta="center"
       color={primary ? colors.accentInk : colors.textPrimary} style={{fontWeight: '500', letterSpacing: 0.84}}>
      {label}
    </P>
  </Btn>
);

const WithdrawSuccessActions = ({onDone, onViewTransaction}: { onDone: () => void; onViewTransaction: () => void }) => (
  <Div gap={10}>
    <ResultActionButton label="Done" primary onPress={onDone}/>
    <ResultActionButton label="View Transaction" onPress={onViewTransaction}/>
  </Div>
);

const WithdrawProcessingHeroIcon = () => {
  const iconStyle = useResultIconPopStyle();

  return (
    <Div alignItems="center" mt={4}>
      <Animated.View style={iconStyle}>
        <ResultIcon
          icon="schedule" color={colors.statusWarn} bg={colors.statusWarnSoft} borderColor={colors.statusWarnSoft}
          size={34} boxSize={72} rounded={999}
        />
      </Animated.View>
    </Div>
  );
};

const WithdrawProcessingHeadline = () => (
  <Div alignItems="center">
    <P fontFamily="POI Aeronaut" fs={28} lh={35} ta="center" color={colors.textPrimary}
       style={{fontWeight: '400', letterSpacing: -0.28}}>
      Your withdrawal is processing
    </P>

    <P fontFamily="POI Aeronaut" fs={14} lh={20.3} color={colors.textSecondary} ta="center" mt={8} maxW={330}>
      It has not been marked as paid yet.
    </P>
  </Div>
);

const WithdrawProcessingStatusCard = () => (
  <Div row alignItems="center" gap={12} bg={colors.bgInset} borderWidth={1}
       borderColor={colors.statusWarnSoft} rounded={14} py={14} px={16}>
    <Div w={40} h={40} rounded={999} bg={colors.statusWarnSoft} alignItems="center" justifyContent="center"
         flexShrink={0}>
      <MaterialIcons name="mail" size={20} color={colors.statusWarn}/>
    </Div>

    <Div flex={1}>
      <P fontFamily="POI Aeronaut" fs={14} lh={21} color={colors.textPrimary} style={{fontWeight: '500'}}>
        Status: Processing
      </P>
      <P fontFamily="POI Aeronaut" fs={12} lh={18} color={colors.textSecondary} mt={2}>
        Check Transaction History for the latest status.
      </P>
    </Div>
  </Div>
);

const WithdrawUncertainHeadline = ({message}: { message?: string }) => (
  <Div alignItems="center">
    <P fontFamily="POI Aeronaut" fs={28} lh={35} ta="center" color={colors.textPrimary}
       style={{fontWeight: '400', letterSpacing: -0.28}}>
      We couldn't confirm your withdrawal
    </P>

    <P fontFamily="POI Aeronaut" fs={14} lh={20.3} color={colors.textSecondary} ta="center" mt={8} maxW={330}>
      {message || 'The request may still be processing.'}
    </P>
  </Div>
);

const WithdrawUncertainStatusCard = () => (
  <Div row alignItems="center" gap={12} bg={colors.bgInset} borderWidth={1}
       borderColor={colors.statusWarnSoft} rounded={14} py={14} px={16}>
    <Div w={40} h={40} rounded={999} bg={colors.statusWarnSoft} alignItems="center" justifyContent="center"
         flexShrink={0}>
      <MaterialIcons name="help-outline" size={20} color={colors.statusWarn}/>
    </Div>

    <Div flex={1}>
      <P fontFamily="POI Aeronaut" fs={14} lh={21} color={colors.textPrimary} style={{fontWeight: '500'}}>
        Status: Unconfirmed
      </P>
      <P fontFamily="POI Aeronaut" fs={12} lh={18} color={colors.textSecondary} mt={2}>
        Check Transaction History before starting another withdrawal. Contact support if it does not appear.
      </P>
    </Div>
  </Div>
);

const WithdrawProcessingSummary = ({lines}: { lines: WithdrawProcessingSummaryLine[] }) => (
  <Div bg={colors.bgElev1} borderWidth={1} borderColor={colors.surfaceBorder} rounded={16} px={18} py={4}>
    {lines.map((line, index) => {
      const isLast = index === lines.length - 1;

      return (
        <Div key={line.label} row justifyContent="space-between" alignItems="center" gap={12} py={12}
             borderBottomWidth={isLast ? 0 : 1} borderBottomColor={colors.hairline}>
          <P fontFamily="POI Aeronaut" fs={13} lh={19.5} color={colors.textSecondary}>{line.label}</P>
          <P fontFamily="POI Aeronaut" fs={14} lh={21} color={colors.textPrimary} ta="right" flex={1}
             fontVariant={['tabular-nums']} style={{fontWeight: '500'}}>
            {line.value}
          </P>
        </Div>
      );
    })}
  </Div>
);

const WithdrawProcessingScreen = (props: WithdrawProcessingScreenProps) => {
  const {amountValue, destinationLabel, methodLabel, reference, onDone, onViewTransaction, trackingSteps} = props;
  const summaryLines: WithdrawProcessingSummaryLine[] = [
    {label: 'Amount', value: formatCurrency(amountValue)},
    {label: 'Requested destination', value: destinationLabel},
    {label: 'Requested method', value: methodLabel}
  ];
  if (reference) summaryLines.push({label: 'Reference', value: reference});

  return (
    <ScrollView style={flowStyles.scroll} contentContainerStyle={flowStyles.processingContent}
                showsVerticalScrollIndicator={false}>
      <Div w="100%" maxW={layout.mobileMax} alignSelf="center" py={8} gap={layout.sectionGap}>
        <WithdrawProcessingHeroIcon/>
        <WithdrawProcessingHeadline/>
        <WithdrawProcessingStatusCard/>
        <WithdrawTrackingTimeline steps={trackingSteps}/>
        <WithdrawProcessingSummary lines={summaryLines}/>

        <Div gap={10}>
          <ResultActionButton label="View Transaction" primary onPress={onViewTransaction}/>
          <ResultActionButton label="Done" onPress={onDone}/>
        </Div>
      </Div>
    </ScrollView>
  );
};

const WithdrawUncertainScreen = (props: WithdrawUncertainScreenProps) => {
  const {amountValue, destinationLabel, methodLabel, message, onDone, onViewTransaction} = props;
  const summaryLines: WithdrawProcessingSummaryLine[] = [
    {label: 'Amount', value: formatCurrency(amountValue)},
    {label: 'Requested destination', value: destinationLabel},
    {label: 'Requested method', value: methodLabel}
  ];

  return (
    <ScrollView style={flowStyles.scroll} contentContainerStyle={flowStyles.processingContent}
                showsVerticalScrollIndicator={false}>
      <Div w="100%" maxW={layout.mobileMax} alignSelf="center" py={8} gap={layout.sectionGap}>
        <WithdrawProcessingHeroIcon/>
        <WithdrawUncertainHeadline message={message}/>
        <WithdrawUncertainStatusCard/>
        <WithdrawProcessingSummary lines={summaryLines}/>

        <Div gap={10}>
          <ResultActionButton label="View Transaction" primary onPress={onViewTransaction}/>
          <ResultActionButton label="Back to My Account" onPress={onDone}/>
        </Div>
      </Div>
    </ScrollView>
  );
};

export const buildSuccessReceiptLines = (amountValue: number, destinationLabel: string, methodLabel: string, feeLabel: string, reference?: string): WithdrawSuccessReceiptLine[] => {
  const lines: WithdrawSuccessReceiptLine[] = [
    {label: 'Requested destination', value: destinationLabel}, {label: 'Requested method', value: methodLabel},
    {label: 'Amount', value: formatCurrency(amountValue)},
    {label: 'Network fee', value: feeLabel, accent: feeLabel !== missingApiValue}
  ];
  if (reference) lines.push({label: 'Reference', value: reference});
  return lines;
};

export const WithdrawResultScreen = (props: WithdrawResultScreenProps) => {
  const {result, amountValue, destinationLabel, methodLabel, feeLabel, netAmountLabel} = props;
  const reference = result.payoff?.transactionId;
  const successReceiptLines = buildSuccessReceiptLines(amountValue, destinationLabel, methodLabel, feeLabel, reference);
  const trackingSteps = result.payoff ? buildPayoutTrackingSteps(result.payoff.status) : [];

  if (result.kind === 'error') {
    return (
      <Div flex={1} bg={colors.signatureBg} position="relative" overflow="hidden">
        <SignaturePatternLayer/>
        <Div flex={1} px={layout.mobileGutter} pt={32} pb={24} zIndex={1}>
          <Div alignItems="center" mt={28} position="relative">
            <ResultGlow tone="error"/>

            <ResultIcon
              icon="error" color={colors.statusLost} bg="rgba(255, 107, 107, 0.14)" borderColor={colors.statusLostSoft}
              rounded={999}
            />
          </Div>

          <Div alignItems="center" mt={20}>
            <P fontFamily="DavisSans-Regular" fs={30} lh={36} ta="center" color={colors.textPrimary}>
              Submission Error
            </P>
            <P fontFamily="DavisSans-Regular" fs={14} lh={20.3} color={colors.textSecondary} ta="center" mt={10}
               maxW={320}>
              {result.message || 'Your withdrawal could not be completed. Please check your information and re-enter to try again.'}
            </P>
          </Div>

          <Div flex={1} minH={16}/>

          <Div gap={layout.methodGap}>
            <PrimaryButton label="Try Again" onPress={props.onRetry}/>
            <OutlineButton label="Back to My Account" onPress={props.onDone}/>
          </Div>
        </Div>
      </Div>
    );
  }

  if (result.kind === 'processing') {
    return (
      <WithdrawProcessingScreen
        amountValue={amountValue} destinationLabel={destinationLabel} methodLabel={methodLabel} reference={reference}
        onDone={props.onDone} onViewTransaction={props.onViewTransaction} trackingSteps={trackingSteps}
      />
    );
  }

  if (result.kind === 'uncertain') {
    return (
      <WithdrawUncertainScreen
        amountValue={amountValue} destinationLabel={destinationLabel} methodLabel={methodLabel}
        message={result.message} onDone={props.onDone} onViewTransaction={props.onViewTransaction}
      />
    );
  }

  return (
    <SignatureResultSurface scroll>
      <WithdrawSuccessHeroIcon/>
      <WithdrawSuccessAmountHeadline netAmountLabel={netAmountLabel}/>
      <WithdrawSuccessReceiptSummary lines={successReceiptLines}/>
      <WithdrawTrackingTimeline steps={trackingSteps}/>

      <Div flex={1} minH={16}/>
      <WithdrawSuccessActions onDone={props.onDone} onViewTransaction={props.onViewTransaction}/>
    </SignatureResultSurface>
  );
};
